import React, { useState } from 'react'

import { DocumentProspectingStep, ProspectingBaseFormValues } from '../../../models/DocumentProspecting'

import {
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingCurrentUser,
  selectDocumentProspectingCurrentUserLoading,
  selectDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'
import { useAppSelector } from '../../../store/hooks'

import { ProspectingIntegrationSelector } from './ProspectingIntegrationSelector'
import { ProspectingDetailsForm } from './ProspectingDetailsForm'
import { ProspectingBaseForm } from './ProspectingBaseForm'
import { ProspectingSuccess } from './ProspectingSuccess'
import { useDocumentProspectingEligibleIntegrations } from './hooks'

import './DocumentProspecting.scss'

export const DocumentProspecting = () => {
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)
  const currentUser = useAppSelector(selectDocumentProspectingCurrentUser)
  const currentUserLoading = useAppSelector(selectDocumentProspectingCurrentUserLoading)
  const eligibleIntegrations = useDocumentProspectingEligibleIntegrations()

  if (!baseFormValues || !currentUser || currentUserLoading) {
    return null
  }

  return <DocumentProspectingSteps baseFormValues={baseFormValues} hasIntegrationOptions={eligibleIntegrations.length > 0} />
}

interface DocumentProspectingStepsProps {
  baseFormValues: ProspectingBaseFormValues
  hasIntegrationOptions: boolean
}

const DocumentProspectingSteps: React.FC<DocumentProspectingStepsProps> = ({ baseFormValues, hasIntegrationOptions }) => {
  const serviceId = useAppSelector(selectDocumentProspectingServiceId)

  const [activeStep, setActiveStep] = useState<DocumentProspectingStep>(
    hasIntegrationOptions ? DocumentProspectingStep.INTEGRATION : DocumentProspectingStep.BASE_FORM,
  )

  const handleComplete = () => {
    setActiveStep((prevState) =>
      prevState === DocumentProspectingStep.BASE_FORM && !serviceId ? DocumentProspectingStep.SUCCESS : ((prevState + 1) as DocumentProspectingStep),
    )
  }

  const handleActivate = (prospectingStep: DocumentProspectingStep) => {
    setActiveStep(prospectingStep)
  }

  const handleRestart = () => {
    setActiveStep(DocumentProspectingStep.BASE_FORM)
  }

  if (activeStep === DocumentProspectingStep.SUCCESS) {
    return (
      <div className='h-100 w-100 d-flex flex-column align-items-center justify-content-center'>
        <ProspectingSuccess onRestart={handleRestart} />
      </div>
    )
  }

  return (
    <div className='h-100 w-100 d-flex flex-column align-items-center justify-content-center'>
      <div className='d-flex flex-column zocks-gap-3'>
        <div className='d-flex flex-column gap-2'>
          <h2>Review before creating contact</h2>
          <span className='paragraph-medium'>
            {hasIntegrationOptions
              ? 'Your AI Assistant extracted these details. Add missing information and choose where to save.'
              : 'Your AI Assistant extracted these details. Add missing information to create the contact in Zocks.'}
          </span>
        </div>
        <div className='d-flex flex-column zocks-gap-2 document-prospecting-form-container position-relative'>
          <div className='document-prospecting-form-divider zocks-bg-color-gray-300' />
          {hasIntegrationOptions ? <ProspectingIntegrationSelector activeStep={activeStep} onComplete={handleComplete} onActivate={handleActivate} /> : null}
          <ProspectingBaseForm activeStep={activeStep} onComplete={handleComplete} onActivate={handleActivate} initialBaseFormValues={baseFormValues} />
          {serviceId ? <ProspectingDetailsForm activeStep={activeStep} onComplete={handleComplete} onActivate={handleActivate} /> : null}
        </div>
      </div>
    </div>
  )
}
